import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Message } from "semantic-ui-react";

const POSTCODE_SCRIPT_URL =
  "https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";

// Daum 우편번호 스크립트 동적 로드 — promise 캐시로 1회만 로드.
// 실패 시 캐시를 비워 다음 open 때 재시도 가능하게 한다.
let postcodeScriptPromise = null;
const loadPostcodeScript = () => {
  if (window.daum && window.daum.Postcode) return Promise.resolve();
  if (postcodeScriptPromise) return postcodeScriptPromise;
  postcodeScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = POSTCODE_SCRIPT_URL;
    script.onload = () => resolve();
    script.onerror = () => {
      postcodeScriptPromise = null;
      reject(new Error("postcode script load failed"));
    };
    document.body.appendChild(script);
  });
  return postcodeScriptPromise;
};

/**
 * Daum 우편번호 서비스 주소 검색 모달 (API 키 불필요).
 * 주소 선택 시 onSelect(주소 문자열) 호출 후 자동으로 닫힌다.
 * 주소 문자열 = 도로명주소(없으면 지번주소) + (건물명 있으면 " (건물명)").
 *
 * @param open      모달 열림 여부
 * @param onClose   닫기 콜백
 * @param onSelect  (address: string) => void
 */
const AddressSearchModal = ({ open, onClose, onSelect }) => {
  const containerRef = useRef(null);
  const [loadError, setLoadError] = useState(false);

  // 열릴 때마다 embed 새로 그림 (이전 선택으로 내부 상태가 소진되므로).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadError(false);
    loadPostcodeScript()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        containerRef.current.innerHTML = "";
        new window.daum.Postcode({
          oncomplete: data => {
            const base = data.roadAddress || data.jibunAddress || "";
            const address = data.buildingName
              ? `${base} (${data.buildingName})`
              : base;
            onSelect(address);
            onClose();
          },
          width: "100%",
          height: "100%"
        }).embed(containerRef.current);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 모바일 뒤로가기 / iOS Safari swipe-back 시 페이지를 떠나지 않고 모달만 닫히게.
  // 모달 열릴 때 dummy history entry 푸시 → popstate 발생하면 onClose 호출.
  // X / 항목 선택 / 외부 클릭 등 다른 경로로 닫히면 cleanup 에서 dummy entry 를
  // history.back() 으로 회수 (사용자가 다음에 뒤로가기 누르면 정상적으로 이전 페이지로).
  useEffect(() => {
    if (!open) return;
    window.history.pushState({ dbrsModal: true }, "");
    let consumedByPop = false;
    const onPopState = () => {
      consumedByPop = true;
      onClose();
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      if (!consumedByPop && window.history.state && window.history.state.dbrsModal) {
        window.history.back();
      }
    };
  }, [open, onClose]);

  return (
    <Modal closeIcon open={!!open} onClose={onClose} size="small">
      <Modal.Header>주소 검색</Modal.Header>
      <Modal.Content>
        {loadError ? (
          <Message negative size="small">
            <p>주소 검색 서비스를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.</p>
          </Message>
        ) : (
          <div ref={containerRef} style={{ width: "100%", height: 450 }} />
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>닫기</Button>
      </Modal.Actions>
    </Modal>
  );
};

export default AddressSearchModal;
